import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Tree from '../../src/components/Tree/Tree';
import createFakeEvent from '../../src/lib/createFakeEvent';
import { addItem, addPlaceholder, convertJsonToTree, deleteItem, getItem, updateKey, updateValue } from './json-util';
import JsonInput from './JsonInput';

const COLUMNS = ['key'];

export default class JsonEditor extends Component {

  static propTypes = {
    className: PropTypes.string,
    data: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    editable: PropTypes.bool,
    onChange: PropTypes.func,
    onExpandCollapse: PropTypes.func,
  };

  static defaultProps = {
    editable: true,
  };

  constructor(props) {
    super(props);
    const data = convertJsonToTree(this.props.data);
    this.state = {
      data: data ? [data] : undefined,
      editingCell: undefined,
      editingKey: undefined,
      editingValue: undefined,
      editingLabel: undefined,
      expanded: {},
    };
    this.stoppedEditing = 0;
  }

  checkIfDataChanged = (prev, current) => {
    if (typeof prev === 'object') {
      const prevString = JSON.stringify(prev);
      const currentString = JSON.stringify(current);
      return prevString !== currentString;
    }
    return prev !== current;
  };

  componentDidUpdate(nextProps) {
    if (this.checkIfDataChanged(nextProps.data, this.props.data)) {
      this.setState({
        data: [convertJsonToTree(nextProps.data)],
      });
    }
  }

  onAddPlaceholder = key => {
    const cell = key.split('.').map(index => parseInt(index, 10)).slice(0, -1);
    const expandedKey = cell.join('.');

    this.setState({
      ...this.state,
      data: addPlaceholder(this.state.data, key, this.state.expanded[expandedKey]),
      expanded: {
        ...this.state.expanded,
        [expandedKey]: true,
      },
    });
  };

  onDelete = e => {
    const { data, onChange } = this.props;
    const { editingCell } = this.state;

    if (onChange) {
      const changed = deleteItem(data, editingCell);

      this.props.onChange(createFakeEvent(changed, e));
    }
  };

  onBlur = e => {
    const { editingCell, editingKey, editingType, editingValue } = this.state;

    this.stopEditing();
    if (editingValue === '') {
      this.onDelete(e);
    } else if (
      editingType !== 'object' &&
      editingType !== 'array' &&
      editingValue !== undefined &&
      this.props.onChange
    ) {
      const changed = updateValue(
        this.props.data,
        editingCell,
        editingKey,
        editingValue
      );

      this.props.onChange(createFakeEvent(changed, e));
    }
  };

  onBlurLabel = e => {
    const { editingCell, editingKey, editingLabel } = this.state;

    this.stopEditing();
    if (editingLabel === '') {
      this.onDelete(e);
    } else if (editingLabel !== undefined && this.props.onChange) {
      const changed = updateKey(this.props.data, editingCell, editingKey, editingLabel);

      this.props.onChange(createFakeEvent(changed, e));
    }
  };

  onChange = e => this.setState({ editingValue: e.target.value });

  onChangeLabel = e => this.setState({ editingLabel: e.target.value});

  onChangeType = e => {
    const key = e.target.dataset.key;
    const name = e.target.dataset.name;
    const clone = addItem(this.props.data, key, name, e.target.value);

    this.stopEditing();
    if (this.props.onChange) {
      this.props.onChange(createFakeEvent(clone, e));
    }
  };

  onClick = (e, column, cell) => {
    const delta = new Date().getTime() - this.stoppedEditing;

    if (delta > 300) { // HACK to prevent onClick from being called directly after an onBlur. Should find a better way to do this
      const item = e.target.value;
      const indexes = cell.split('.').map(k => parseInt(k, 10)).slice(1, -2);
      const parent = getItem(this.props.data, indexes);
      const label = !Array.isArray(parent) && e.currentTarget.querySelector('.key');
      const value = e.currentTarget.querySelector('.value');
      const labelRect = label && label.getBoundingClientRect();
      const valueRect = value && value.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;
      const insideLabel =
        label &&
        x >= labelRect.x &&
        x <= labelRect.x + labelRect.width &&
        y >= labelRect.y &&
        y <= labelRect.y + labelRect.height;
      const insideValue =
        value &&
        x >= valueRect.x &&
        x <= valueRect.x + valueRect.width &&
        y >= valueRect.y &&
        y <= valueRect.y + valueRect.height;
      const add = valueRect && x > valueRect.x + valueRect.width;

      if (add) {
        this.onAddPlaceholder(cell);
      } else if (this.props.editable && (insideLabel || insideValue)) {
        this.setState({
          editingCell: cell,
          editingLabel: insideLabel ? item.key : undefined,
          editingKey: item.key,
          editingType: item.type,
        });
      }
    }
  };

  onExpandCollapse = (e, key, state, expanded) => {
    this.setState({ expanded });
    if (this.props.onExpandCollapse) {
      this.props.onExpandCollapse(e, key, state, expanded);
    }
  };

  onKeyDown = e => {
    if (e.keyCode === 13) {
      this.onBlur(e, 1);
    } else if (e.keyCode === 27) {
      this.stopEditing();
    }
  };

  onKeyDownLabel = e => {
    if (e.keyCode === 13) {
      this.onBlurLabel(e, 1);
    } else if (e.keyCode === 27) {
      this.stopEditing();
    }
  };

  getTypeSelector = (key, name) =>
    <select onChange={this.onChangeType} data-key={key} data-name={name}>
      <option value="unknown-type">Select type</option>
      <option value="array">array</option>
      <option value="boolean">boolean</option>
      <option value="object">object</option>
      <option value="number">number</option>
      <option value="string">string</option>
    </select>;

  itemRenderer = (item, column, columnIndex, cell) => {
    let label;
    let editor;

    if (
      cell === this.state.editingCell &&
      this.state.editingLabel !== undefined
    ) {
      const value =
        this.state.editingLabel !== undefined
          ? this.state.editingLabel
          : item.key;

      label = (
        <span className='key'>
          <JsonInput
            className='key'
            value={value}
            onChange={this.onChangeLabel}
            onKeyDown={this.onKeyDownLabel}
            onBlur={this.onBlurLabel}
          />
        </span>
      );
    } else if (item.type === 'unknown-type' && item.value === undefined) {
      editor = this.getTypeSelector(cell, item.key);
    } else if (cell === this.state.editingCell) {
      const value =
        this.state.editingValue !== undefined
          ? this.state.editingValue
          : item.value;

      editor = (
        <JsonInput
          className={item.type}
          value={value}
          onBlur={this.onBlur}
          onChange={this.onChange}
          onKeyDown={this.onKeyDown}
        />
      );
    }
    return (
      <span className={item.type}>
        {item.key !== undefined &&
          (label || <span className='key'>{item.key}</span>)}
        {editor || <span className='value'>{item.value}</span>}
      </span>
    );
  };

  stopEditing = () => {
    this.stoppedEditing = new Date().getTime();
    this.setState({
      editingCell: undefined,
      editingKey: undefined,
      editingValue: undefined,
      editingLabel: undefined,
      editingType: undefined,
    });
  };

  render() {
    const { className, editable, ...rest } = this.props;
    const { data, expanded } = this.state;

    return (
      <div className={cx('json-editor', className, { editable })}>
        <Tree
          expanded={expanded}
          {...rest}
          items={data}
          columns={COLUMNS}
          itemRenderer={this.itemRenderer}
          onClick={this.onClick}
          iconName='play-arrow'
          onExpandCollapse={this.onExpandCollapse}
          loadingText=''
        />
      </div>
    );
  }
}
